import { useTheme } from '../context/ThemeContext';
import { useState, useEffect } from 'react';
import { getTransactions, getBudgetSummary, getBalances, getCategories, getIncome, getHousehold, getSavingsAdvisor } from '../api/client';


const fmt = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (a, b) => b === 0 ? 0 : Math.min(100, Math.round((a / b) * 100));

const ProgressBar = ({ value, max, color = null, height = 6 }) => {
  const { COLORS } = useTheme();
  const resolvedColor = color || COLORS.accent;
  const p = pct(value, max);
  const barColor = p > 90 ? COLORS.red : p > 70 ? COLORS.amber : resolvedColor;
  return (
    <div style={{ height, background: COLORS.border, borderRadius: 99, overflow: "hidden" }}>
      <div style={{ width: `${p}%`, height: "100%", background: barColor, borderRadius: 99, transition: "width 0.6s ease" }} />
    </div>
  );
};

const fmtDate = (dateStr) => {
  const [year, month, day] = (dateStr || '').split('T')[0].split('-');
  if (!year) return '—';
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getPeriodStart = (monthStart = 26) => {
  const today = new Date();
  if (today.getDate() >= monthStart) return new Date(today.getFullYear(), today.getMonth(), monthStart);
  return new Date(today.getFullYear(), today.getMonth() - 1, monthStart);
};

const getPeriodEnd = (monthStart = 26) => {
  const start = getPeriodStart(monthStart);
  return new Date(start.getFullYear(), start.getMonth() + 1, monthStart - 1);
};

const getPeriodDateStrings = (monthStart = 26) => {
  const s = getPeriodStart(monthStart);
  const e = getPeriodEnd(monthStart);
  return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] };
};

const getWeeks = (monthStart = 26) => {
  const periodStart = getPeriodStart(monthStart);
  const periodEnd   = getPeriodEnd(monthStart);
  const firstThursday = new Date(periodStart);
  while (firstThursday.getDay() !== 4) firstThursday.setDate(firstThursday.getDate() + 1);
  const weeks = [];
  if (firstThursday > periodStart) {
    const partialEnd = new Date(firstThursday);
    partialEnd.setDate(firstThursday.getDate() - 1);
    weeks.push({
      label: 'Week 1',
      start: periodStart.toISOString().split('T')[0],
      end:   partialEnd.toISOString().split('T')[0],
      startLabel: periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      endLabel:   partialEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      startDate: new Date(periodStart), endDate: new Date(partialEnd),
    });
  }
  let weekStart = new Date(firstThursday);
  while (weekStart <= periodEnd) {
    const weekEnd    = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const clampedEnd = weekEnd > periodEnd ? new Date(periodEnd) : weekEnd;
    weeks.push({
      label: `Week ${weeks.length + 1}`,
      start: weekStart.toISOString().split('T')[0],
      end:   clampedEnd.toISOString().split('T')[0],
      startLabel: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      endLabel:   clampedEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      startDate: new Date(weekStart), endDate: new Date(clampedEnd),
    });
    weekStart = new Date(clampedEnd);
    weekStart.setDate(clampedEnd.getDate() + 1);
  }
  return weeks;
};

const getCurrentWeekIndex = (weeks) => {
  const today = new Date().toISOString().split('T')[0];
  for (let i = 0; i < weeks.length; i++) {
    if (today >= weeks[i].start && today <= weeks[i].end) return i;
  }
  return weeks.length - 1;
};

const dayOfMonthInWeek = (day, startDate, endDate) => {
  const d = new Date(startDate);
  while (d <= endDate) {
    if (d.getDate() === day) return true;
    d.setDate(d.getDate() + 1);
  }
  return false;
};

const weekdayInWeek = (weekdayName, startDate, endDate) => {
  const DAYS = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
  const target = DAYS[weekdayName];
  if (target === undefined) return false;
  const d = new Date(startDate);
  while (d <= endDate) {
    if (d.getDay() === target) return true;
    d.setDate(d.getDate() + 1);
  }
  return false;
};

const getExpectedIncome = (incomeSources, weekStartDate, weekEndDate) => {
  const items = [];
  incomeSources.forEach(src => {
    const amt = parseFloat(src.amount || 0);
    if (src.schedule_type === 'weekly') {
      if (weekdayInWeek(src.week_day, weekStartDate, weekEndDate))
        items.push({ id: src.id, name: src.name, amount: amt });
    } else if (src.schedule_type === 'biweekly') {
      if (weekdayInWeek(src.week_day, weekStartDate, weekEndDate)) {
        const DAYS = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
        const target = DAYS[src.week_day];
        const d = new Date(weekStartDate);
        while (d.getDay() !== target) d.setDate(d.getDate() + 1);
        const anchor = new Date(2020, 0, 2);
        const diffWeeks = Math.round((d - anchor) / (7 * 24 * 60 * 60 * 1000));
        if (diffWeeks % 2 === 0) items.push({ id: src.id, name: src.name, amount: amt });
      }
    } else if (src.schedule_type === 'monthly') {
      if (src.month_day && dayOfMonthInWeek(parseInt(src.month_day), weekStartDate, weekEndDate))
        items.push({ id: src.id, name: src.name, amount: amt });
    } else if (src.schedule_type === 'twiceMonthly') {
      let added = false;
      if (src.month_day_1 && dayOfMonthInWeek(parseInt(src.month_day_1), weekStartDate, weekEndDate)) {
        items.push({ id: src.id, name: src.name, amount: amt }); added = true;
      }
      if (!added && src.month_day_2 && dayOfMonthInWeek(parseInt(src.month_day_2), weekStartDate, weekEndDate))
        items.push({ id: src.id, name: src.name, amount: amt });
    }
  });
  return items;
};

const getExpectedFixedBills = (categories, weekStartDate, weekEndDate) => {
  const items = [];
  categories.forEach(cat => {
    if (cat.is_active === false || cat.exclude_from_budget) return;
    if (cat.type === 'fixed' && cat.due_day &&
        dayOfMonthInWeek(parseInt(cat.due_day), weekStartDate, weekEndDate)) {
      items.push({ name: cat.name, amount: parseFloat(cat.amount), id: cat.id });
    }
  });
  return items;
};

const SavingsAdvisor = ({ COLORS }) => {
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [selectedWk,  setSelectedWk]  = useState(0); // index of selected week

  useEffect(() => {
    (async () => {
      try {
        const res = await getSavingsAdvisor();
        setData(res.data);
      } catch (e) { /* silent fail */ }
      finally { setLoading(false); }
    })();
  }, []);

  const verdictStyle = {
    good:    { color: COLORS.green, bg: COLORS.green + '18', border: COLORS.green + '44', icon: '✅', label: 'Good Week to Save'  },
    caution: { color: COLORS.amber, bg: COLORS.amber + '18', border: COLORS.amber + '44', icon: '⚠️', label: 'Save Carefully'     },
    hold:    { color: COLORS.red,   bg: COLORS.red   + '18', border: COLORS.red   + '44', icon: '🛑', label: 'Hold Off This Week' },
  };

  if (loading) return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0 }}>Analyzing cash flow...</p>
    </div>
  );
  if (!data) return null;

  const fmt2   = (n) => `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const verdict = data.recommendation?.verdict || 'caution';
  const vs      = verdictStyle[verdict] || verdictStyle.caution;
  const weeks   = data.weeks || [];
  const active  = weeks[selectedWk] || weeks[0];

  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>💡 Savings Advisor</h3>
          <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '3px 0 0' }}>Full month cash flow outlook</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>Checking</div>
          <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 15 }}>{fmt2(data.checkingBalance)}</div>
        </div>
      </div>

      {/* AI verdict */}
      <div style={{ background: vs.bg, border: `1px solid ${vs.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 16 }}>{vs.icon}</span>
          <span style={{ color: vs.color, fontWeight: 700, fontSize: 14 }}>{vs.label}</span>
          {data.recommendation?.maxSafe > 0 && (
            <span style={{ marginLeft: 'auto', color: vs.color, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13 }}>
              Max safe: {fmt2(data.recommendation.maxSafe)}
            </span>
          )}
        </div>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>{data.recommendation?.message}</p>
      </div>

      {/* Week selector tiles */}
      {weeks.length > 0 && (
        <div>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>Tap a week for details</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {weeks.map((w, i) => {
              const isActive = i === selectedWk;
              const balColor = w.projectedEnd < 200 ? COLORS.red : w.projectedEnd < 500 ? COLORS.amber : COLORS.green;
              return (
                <div key={i} onClick={() => setSelectedWk(i)}
                  style={{ flex: '1 1 0', minWidth: 80, background: isActive ? COLORS.accent + '22' : COLORS.bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center', cursor: 'pointer',
                    border: `1px solid ${isActive ? COLORS.accent : w.projectedEnd < 200 ? COLORS.red + '44' : COLORS.border}`,
                    transition: 'all 0.15s' }}>
                  <div style={{ color: isActive ? COLORS.accent : COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>{w.shortLabel}</div>
                  <div style={{ color: balColor, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>
                    {fmt2(w.projectedEnd)}
                  </div>
                  {w.totalBills > 0 && <div style={{ color: COLORS.red, fontSize: 10, marginTop: 2 }}>-{fmt2(w.totalBills)} bills</div>}
                  {w.estimatedVarSpend > 0 && <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 1 }}>~-{fmt2(w.estimatedVarSpend)} var</div>}
                </div>
              );
            })}
          </div>

          {/* Detail panel for selected week */}
          {active && (
            <div style={{ background: COLORS.bg, borderRadius: 10, padding: '12px 14px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ color: COLORS.text, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>{active.label}</div>

              {/* Income */}
              {active.incomeItems?.length > 0 && active.incomeItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                  <span style={{ color: COLORS.green, fontSize: 13 }}>💰 {item.name}</span>
                  <span style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600 }}>+{fmt2(item.amount)}</span>
                </div>
              ))}

              {/* Bills */}
              {active.billItems?.length > 0 && active.billItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${COLORS.border}` }}>
                  <span style={{ color: COLORS.textMuted, fontSize: 13 }}>🧾 {item.name}</span>
                  <span style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600 }}>-{fmt2(item.amount)}</span>
                </div>
              ))}

              {active.incomeItems?.length === 0 && active.billItems?.length === 0 && (
                <div style={{ color: COLORS.textMuted, fontSize: 13 }}>No scheduled income or bills this week.</div>
              )}

              {/* Totals */}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>Income</div>
                  <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>+{fmt2(active.totalIncome)}</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>Fixed Bills</div>
                  <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>-{fmt2(active.totalBills)}</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>Est. Var</div>
                  <div style={{ color: COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>~-{fmt2(active.estimatedVarSpend)}</div>
                </div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>End Bal</div>
                  <div style={{ color: active.projectedEnd < 200 ? COLORS.red : active.projectedEnd < 500 ? COLORS.amber : COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt2(active.projectedEnd)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Weekly = () => {
  const { COLORS } = useTheme();
  const [transactions,  setTransactions]  = useState([]);
  const [summary,       setSummary]       = useState(null);
  const [balances,      setBalances]      = useState(null);
  const [categories,    setCategories]    = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [household,     setHousehold]     = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [aiInsights,    setAiInsights]    = useState([]);
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiFetched,     setAiFetched]     = useState(false);
  const [aiWeek,        setAiWeek]        = useState(null);

  const monthStart = household?.month_start || 26;
  const weeks = getWeeks(monthStart);
  const [selectedWeek, setSelectedWeek] = useState(0);

  const handleWeekChange = (i) => {
    setSelectedWeek(i);
    setAiFetched(false);
    setAiInsights([]);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [txRes, sumRes, balRes, catRes, incRes, hhRes] = await Promise.all([
          getTransactions(), getBudgetSummary(), getBalances(), getCategories(), getIncome(), getHousehold()
        ]);
        const txData  = txRes.data;
        const sumData = sumRes.data;
        const balData = balRes.data;
        const catData = catRes.data;
        const incData = incRes.data;
        const hhData  = hhRes.data;
        setTransactions(Array.isArray(txData)  ? txData  : txData?.transactions || txData?.data || []);
        setSummary(Array.isArray(sumData) ? null : sumData?.data || sumData || null);
        setBalances(Array.isArray(balData) ? null : balData?.data || balData || null);
        setCategories(Array.isArray(catData) ? catData : catData?.data || []);
        setIncomeSources(Array.isArray(incData) ? incData : incData?.data || []);
        const hh = Array.isArray(hhData) ? null : hhData?.data || hhData || null;
        setHousehold(hh);
        const ms = hh?.month_start || 26;
        const ws = getWeeks(ms);
        setSelectedWeek(getCurrentWeekIndex(ws));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: 'center', paddingTop: 80 }}>Loading...</div>
  );

  const week        = weeks[selectedWeek] || weeks[weeks.length - 1];
  const period      = getPeriodDateStrings(monthStart);
  const categoryMap = Object.fromEntries(categories.map(c => [c.id, c]));

  const weekTxns     = transactions.filter(t => t.date >= week.start && t.date <= week.end);
  const weekExpenses = weekTxns.filter(t => t.type === 'expense');
  const weekIncome   = weekTxns.filter(t => t.type === 'income');
  const actualExpenses = weekExpenses.reduce((s, t) => s + parseFloat(t.amount), 0);
  // Only count income matched to a known income source — avoids counting transfers/refunds
  const actualIncome = weekIncome
    .filter(t => t.income_source_id != null)
    .reduce((s, t) => s + parseFloat(t.amount), 0);

  // Full period expenses for monthly variable tracking
  const periodExpenses = transactions.filter(t =>
    t.type === 'expense' && t.date >= period.start && t.date <= period.end
  );

  const expectedIncomeItems = getExpectedIncome(incomeSources, week.startDate, week.endDate);
  const expectedIncomeTotal = expectedIncomeItems.reduce((s, i) => s + i.amount, 0);

  const fixedDueItems = categories
    .filter(c => c.type === 'fixed' && c.is_active !== false && !c.exclude_from_budget && c.due_day &&
      dayOfMonthInWeek(parseInt(c.due_day), week.startDate, week.endDate))
    .map(cat => {
      const paidTxns = periodExpenses.filter(t => t.category_id === cat.id);
      const paidAmt  = paidTxns.reduce((s, t) => s + parseFloat(t.amount), 0);
      return { name: cat.name, amount: parseFloat(cat.amount), id: cat.id, due_day: cat.due_day, paidAmt, paid: paidAmt > 0 };
    });
  const fixedDueTotal = fixedDueItems.reduce((s, i) => s + i.amount, 0);
  const paidFixed     = fixedDueItems.reduce((s, i) => s + i.paidAmt, 0);

  // Variable categories — month + week tracker
  const variableTrackers = categories
    .filter(c => c.type === 'variable' && c.is_active !== false && !c.exclude_from_budget)
    .map(cat => {
      const monthlyBudget = parseFloat(cat.amount || 0);
      const weeklySlice   = monthlyBudget / 4;
      const spentMonth    = periodExpenses.filter(t => t.category_id === cat.id).reduce((s, t) => s + parseFloat(t.amount), 0);
      const spentWeek     = weekExpenses.filter(t => t.category_id === cat.id).reduce((s, t) => s + parseFloat(t.amount), 0);
      const remaining     = Math.max(0, monthlyBudget - spentMonth);
      return { cat, monthlyBudget, weeklySlice, spentMonth, spentWeek, remaining };
    })
    .filter(v => v.monthlyBudget > 0 || v.spentMonth > 0);

  // Misc spending this period grouped by category
  const miscSpend = {};
  periodExpenses.forEach(t => {
    const cat = categoryMap[t.category_id];
    if (!cat || cat.type !== 'misc') return;
    miscSpend[cat.name] = (miscSpend[cat.name] || 0) + parseFloat(t.amount);
  });

  const totalCash = balances?.summary?.totalCash || 0;

  const runWeeklyAI = async () => {
    setAiLoading(true); setAiFetched(true); setAiWeek(week.label);
    try {
      const topSpend = weekExpenses
        .reduce((acc, t) => {
          const cat = categoryMap[t.category_id]?.name || 'Uncategorized';
          acc[cat] = (acc[cat] || 0) + parseFloat(t.amount);
          return acc;
        }, {});
      const topStr = Object.entries(topSpend).sort((a,b) => b[1]-a[1]).slice(0,6)
        .map(([k,v]) => `${k}: $${v.toFixed(0)}`).join(', ');
      const prompt = `You are a household budget analyst. Give 3 short, specific, actionable insights (1 sentence each) about this week's spending.
Week: ${week.startLabel} – ${week.endLabel}. Total spent: $${actualExpenses.toFixed(0)}, income received: $${actualIncome.toFixed(0)}.
Top spending categories this week: ${topStr || 'none yet'}.
Cash available: $${totalCash.toFixed(0)}.
Return ONLY a JSON array of 3 strings, no preamble, no markdown. Example: ["insight 1","insight 2","insight 3"]`;

      const res = await fetch("/api/household/ai-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('ledger_token')}` },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const parsed = JSON.parse(text.replace(/```json|```/g, "").trim());
      setAiInsights(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      setAiInsights(["Unable to load analysis — check your connection."]);
    } finally { setAiLoading(false); }
  };

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ padding: "20px 16px 10px" }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Weekly Tracker</h2>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "4px 0 0" }}>{week.startLabel} – {week.endLabel}</p>
      </div>

      {/* Week selector */}
      <div style={{ padding: "0 12px 14px", display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none" }}>
        {weeks.map((w, i) => (
          <button key={i} onClick={() => handleWeekChange(i)} style={{ flexShrink: 0, padding: "10px 14px", borderRadius: 12, border: `1px solid ${selectedWeek === i ? COLORS.accent : COLORS.border}`, background: selectedWeek === i ? COLORS.accentGlow : COLORS.surface, color: selectedWeek === i ? COLORS.accent : COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12 }}>
            {w.label}
          </button>
        ))}
      </div>

      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* ── Income ── */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>Income</div>
          <div style={{ display: "flex", gap: 10, marginBottom: expectedIncomeItems.length > 0 ? 14 : 0 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Expected</div>
              <div style={{ color: COLORS.white || '#ffffff', fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 20 }}>
                {expectedIncomeTotal > 0 ? `+${fmt(expectedIncomeTotal)}` : '—'}
              </div>
            </div>
            <div style={{ flex: 1, textAlign: "right" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Received</div>
              <div style={{ color: actualIncome > 0 ? COLORS.green : COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 20 }}>
                {actualIncome > 0 ? `+${fmt(actualIncome)}` : '—'}
              </div>
            </div>
          </div>
          {expectedIncomeItems.length > 0 && (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Source</span>
                <div style={{ display: "flex", gap: 32 }}>
                  <span style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Expected</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Actual</span>
                </div>
              </div>
              {expectedIncomeItems.map((item, i) => {
                const actual = weekIncome
                  .filter(t => t.income_source_id === item.id)
                  .reduce((s, t) => s + parseFloat(t.amount), 0);
                return (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{item.name}</span>
                    <div style={{ display: "flex", gap: 32 }}>
                      <span style={{ color: COLORS.white || '#ffffff', fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600 }}>+{fmt(item.amount)}</span>
                      <span style={{ color: actual > 0 ? COLORS.green : COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600 }}>
                        {actual > 0 ? `+${fmt(actual)}` : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Fixed Bills ── */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>Fixed Bills This Week</div>
          {fixedDueItems.length === 0 ? (
            <div style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "8px 0" }}>No bills due this week</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Total Due</div>
                  <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 20 }}>-{fmt(fixedDueTotal)}</div>
                </div>
                <div style={{ flex: 1, textAlign: "right" }}>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 4 }}>Paid</div>
                  <div style={{ color: paidFixed > 0 ? COLORS.green : COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 20 }}>
                    {paidFixed > 0 ? `-${fmt(paidFixed)}` : '—'}
                  </div>
                </div>
              </div>
              <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {fixedDueItems.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: item.paid ? COLORS.green : COLORS.amber, flexShrink: 0 }} />
                      <div>
                        <div style={{ color: item.paid ? COLORS.textMuted : COLORS.text, fontSize: 12, textDecoration: item.paid ? 'line-through' : 'none' }}>{item.name}</div>
                        {item.due_day && <div style={{ color: COLORS.textMuted, fontSize: 10 }}>Due day {item.due_day}</div>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: item.paid ? COLORS.green : COLORS.red, fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600 }}>
                        {item.paid ? `-${fmt(item.paidAmt)}` : `-${fmt(item.amount)}`}
                      </div>
                      {item.paid && <div style={{ color: COLORS.green, fontSize: 10 }}>✓ paid</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Variable Budget Trackers ── */}
        {variableTrackers.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 14 }}>Variable Budgets</div>
            <div style={{ maxHeight: 420, overflowY: "auto", scrollbarWidth: "none" }}>
            {variableTrackers.map(({ cat, monthlyBudget, weeklySlice, spentMonth, spentWeek, remaining }, i, arr) => {
              const monthPct = pct(spentMonth, monthlyBudget);
              const isOver   = spentMonth > monthlyBudget;
              const barColor = monthPct > 90 ? COLORS.red : monthPct > 70 ? COLORS.amber : COLORS.green;
              return (
                <div key={cat.id} style={{ marginBottom: i < arr.length - 1 ? 20 : 0, paddingBottom: i < arr.length - 1 ? 20 : 0, borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                    <span style={{ color: COLORS.text, fontSize: 14, fontWeight: 700 }}>{cat.name}</span>
                    <span style={{ color: isOver ? COLORS.red : COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                      {fmt(spentMonth)} / {fmt(monthlyBudget)}
                    </span>
                  </div>
                  <ProgressBar value={spentMonth} max={monthlyBudget} color={barColor} height={7} />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                    <div>
                      <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 3 }}>Month left</div>
                      <div style={{ color: isOver ? COLORS.red : COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13 }}>
                        {isOver ? `-${fmt(spentMonth - monthlyBudget)}` : fmt(remaining)}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 3 }}>This week</div>
                      <div style={{ color: spentWeek > weeklySlice ? COLORS.amber : COLORS.text, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13 }}>
                        {spentWeek > 0 ? fmt(spentWeek) : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: 3 }}>Weekly target</div>
                      <div style={{ color: COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{fmt(weeklySlice)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}

        {Object.keys(miscSpend).length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>Misc Spending This Period</div>
            {Object.entries(miscSpend).map(([name, amount], i, arr) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: i < arr.length - 1 ? 10 : 0, marginBottom: i < arr.length - 1 ? 10 : 0, borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <span style={{ color: COLORS.text, fontSize: 13 }}>{name}</span>
                <span style={{ color: COLORS.amber, fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600 }}>-{fmt(amount)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Cash Flow ── */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14 }}>
            <span style={{ fontSize: 15 }}>📈</span>
            <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>Cash Flow</h3>
          </div>
          {[
            { label: "Cash In",       value: actualIncome,   color: COLORS.green, sub: "Paychecks & deposits",   prefix: "+" },
            { label: "Cash Out",      value: actualExpenses, color: COLORS.red,   sub: "All spending this week", prefix: "-" },
            { label: "Available Now", value: totalCash,      color: COLORS.green, sub: "Total across checking",  prefix: "", highlight: true },
          ].map((row, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", marginBottom: i < 2 ? 2 : 0, borderRadius: row.highlight ? 10 : 8, background: row.highlight ? "linear-gradient(135deg, #0f172a, #1e1b4b)" : COLORS.bg, border: row.highlight ? `1px solid ${COLORS.green}44` : `1px solid transparent` }}>
              <div>
                <div style={{ color: row.highlight ? COLORS.green : COLORS.text, fontSize: 13, fontWeight: row.highlight ? 700 : 500 }}>{row.label}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{row.sub}</div>
              </div>
              <div style={{ color: row.color, fontFamily: "'DM Mono', monospace", fontWeight: row.highlight ? 800 : 600, fontSize: row.highlight ? 20 : 15 }}>
                {row.prefix}{fmt(row.value)}
              </div>
            </div>
          ))}
        </div>

        {/* ── Transactions ── */}
        {weekTxns.length > 0 ? (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: "0 0 14px" }}>Transactions</h3>
            <div style={{ maxHeight: 300, overflowY: "auto", scrollbarWidth: "none" }}>
            {weekTxns.map((t, i) => {
              const cat = categoryMap[t.category_id];
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < weekTxns.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{t.description}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                      {fmtDate(t.date)} · {cat?.name || 'Uncategorized'}
                    </div>
                  </div>
                  <span style={{ color: t.type === 'income' ? COLORS.green : t.type === 'transfer' ? COLORS.textMuted : COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>
                    {t.type === 'income' ? '+' : t.type === 'transfer' ? '' : '-'}{fmt(t.amount)}
                  </span>
                </div>
              );
            })}
            </div>
          </div>
        ) : (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "40px 20px", textAlign: "center" }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>No activity yet for {week.label}</p>
          </div>
        )}

        {/* ── Savings Advisor ── */}
        <SavingsAdvisor COLORS={COLORS} />

        {/* ── AI Weekly Analysis ── */}
        <div style={{ background: `linear-gradient(135deg, ${COLORS.surface}, ${COLORS.bg})`, border: `1px solid ${COLORS.accent}33`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: aiInsights.length > 0 ? 14 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>🤖</span>
              <h3 style={{ color: COLORS.accent, fontWeight: 700, fontSize: 15, margin: 0 }}>AI Weekly Analysis</h3>
            </div>
            {!aiFetched && (
              <button onClick={runWeeklyAI} style={{ padding: "6px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>
                Analyze
              </button>
            )}
            {aiFetched && !aiLoading && (
              <button onClick={() => { setAiFetched(false); setAiInsights([]); setTimeout(runWeeklyAI, 50); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>↻</button>
            )}
          </div>
          {aiLoading && <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "10px 0 0", fontStyle: "italic" }}>Analyzing {week.label}...</p>}
          {aiInsights.map((insight, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderTop: `1px solid ${COLORS.border}`, marginTop: i === 0 ? 4 : 0 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: [COLORS.green, COLORS.amber, COLORS.accent][i % 3], flexShrink: 0, marginTop: 5 }} />
              <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>{insight}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Weekly;
